import { useGlobal } from 'hooks/useGlobal';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import numeral from 'numeral';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { cloneDeep, isNumber, isString, merge } from 'lodash';
import Highcharts, {
  Chart,
  ChartSelectionContextObject,
  Options,
  Point,
  PointerEventObject,
  PointOptionsObject,
  SeriesLineOptions,
  SeriesOptionsType,
  SeriesPolygonOptions,
  TooltipPositionerPointObject,
  XAxisPlotBandsOptions,
  XAxisPlotLinesOptions,
} from 'highcharts';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import HighchartsMore from 'highcharts/highcharts-more';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import InsertDriveFile from '@mui/icons-material/InsertDriveFile';

import { Annotation } from 'common/graphqlTypes';
import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import { truncateText } from 'common/util/string';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { useOutsideEvent } from 'hooks/useOutsideEvent';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';

import getScalyrGraphConfig from 'components/common/Graphs/ScalyrGraphConfig';
import { currentChartRefState } from 'components/common/Graphs/graphs.state';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import {
  graphPageRemainingWidthSelector,
  searchLegendWidthSelector,
} from 'components/common/Managers/LayoutStateManager';
import { queryTimeRangeState, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import AnnotationModal from 'components/common/Annotations/AnnotationModal';
import { AnnotationData, AnnotationTooltip } from 'components/common/AnnotationTooltip/AnnotationTooltip';
import ScalyrHighcharts from 'components/common/ScalyrHighcharts';

import { AnnotationStyles, annotationStylesState, highchartsStylesState } from 'styles/stylesProvider';
import './ScalyrGraph.scss';

// Initialize the exporting module
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);
// Initialize the polygon module
HighchartsMore(Highcharts);

type HighchartsPoint = Point & {
  x: number;
  chartX: number;
};

type ContainerPropEvent = Event & {
  nativeEvent: PointerEventObject;
};

type ScalyrGraphProps = {
  seriesData?: Array<Highcharts.SeriesOptionsType>;
  annotationsData?: Array<Annotation>;
  isDarkMode?: boolean;
  recalcBarWidth: () => void;
};

/**
 * Call the exportChart function on the chart instance to allow the user to download a PNG of the current chart
 *
 * We have to do some manipulation and some default settings back into the chart temporarily so the image has a
 * legend to display, and then we remove them after calling the exportChart function to return the chart to
 * its original state.
 *
 * @param chart  The chart instance to fire export event on
 * @param title  The title of the chart (will double as filename)
 */
export const handleExport = (chart: Highcharts.Chart, title: string): void => {
  // Save references to the altered series to be able to reverse changes after the export
  const alteredSeries: Highcharts.Series[] = [];

  const additionalOptions: Options = {
    chart: {
      marginTop: undefined,
      marginBottom: undefined,
      spacingTop: 10,
      spacingBottom: 15,
      spacingLeft: 10,
      spacingRight: 10,
      width: 1200, // TODO FRONT-4839: Customizable Export to PNG for HighCharts Graphs
      height: 480,
    },
    plotOptions: {
      pie: {
        size: null,
      },
    },
    title: {
      text: title || K.PRODUCT_NAME + ' Chart',
      align: 'left',
      style: {
        display: 'inline',
        'font-weight': 'bold',
        'font-size': '14px',
      },
    },
    legend: {
      enabled: true,
    },
  };

  // Only graphed series should be visible in the legend
  chart.series.forEach((series) => {
    if (!series.visible) {
      // ignoring TS as this seems to be a types bug; nothing mentioned in the docs about `type` field being mandatory within `.update(…)`
      // @ts-expect-error Property 'type' is missing in type '{ showInLegend: false; }'
      series.update({ showInLegend: false }, false);
      alteredSeries.push(series);
    }
  });

  chart.exportChartLocal(
    {
      filename: title.replace(/ /g, '_').toLowerCase(),
      type: 'image/png',
      fallbackToExportServer: false,
    },
    additionalOptions,
  );

  // Reverse changes after the export
  alteredSeries.forEach((series, index) => {
    if (index === alteredSeries.length - 1) {
      // @ts-expect-error Property 'type'
      series.update({ showInLegend: true }, true); // Allow chart redraw only when we finally revert the last change in the series
    } else {
      // @ts-expect-error Property 'type'
      series.update({ showInLegend: true }, false);
    }
  });
};

type Vector = [number | string, number | null];

const isVector = (value: unknown): value is Vector => {
  if (!Array.isArray(value)) {
    return false;
  }

  const [x, y] = value;

  return (isNumber(x) || isString(x)) && (isNumber(y) || y === null);
};

type LinePoint = number | Vector | null | PointOptionsObject;

function isLineWithData(series: SeriesOptionsType): series is SeriesLineOptions & { data: Array<LinePoint> } {
  return series.type === 'line' && Boolean(series.data);
}

const addMarkerToSinglePoint = (series: SeriesOptionsType): SeriesOptionsType => {
  if (!isLineWithData(series)) {
    return series;
  }

  const { data } = series;

  if (data.length === 1 && isVector(data[0])) {
    const [x, y] = data[0];

    return {
      ...series,
      data: [
        {
          x: isString(x) ? parseFloat(x) : x,
          y,
          marker: {
            enabled: true,
          },
        },
      ],
    };
  }

  return series;
};

const prepareSeries = (
  series: Array<SeriesPolygonOptions>,
  seriesData?: Array<SeriesOptionsType>,
): Array<SeriesOptionsType> => {
  const mergedSeries = [...series, ...(seriesData || [])];

  return mergedSeries.map(addMarkerToSinglePoint);
};

/**
 * Scalyr graph component is used on the Search Graph page.
 *
 */
function ScalyrGraph({ seriesData, annotationsData, recalcBarWidth }: ScalyrGraphProps): JSX.Element {
  const chartRef = useRef<{ chart: Chart; container: RefObject<HTMLDivElement> }>(null); // used for exporting our chart
  const mouseDownRef = useRef<number | null>(null);
  const setCurrentChartRef = useSetRecoilState(currentChartRefState);
  const [selectionRange, setSelectionRange] = useGlobal(K.GLOBAL_KEY.CHART_SELECTION_RANGE);
  const { start: rangeStart, end: rangeEnd } = selectionRange;
  const setSearchParams = useSetSearchParams();
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const yScale = useRecoilValue(searchParamState(K.URL_PARAM.Y_SCALE));
  const remainingWidth = useRecoilValue(graphPageRemainingWidthSelector);
  const searchGraphLegendWidth = useRecoilValue(searchLegendWidthSelector);
  const highchartsStyles = useRecoilValue(highchartsStylesState);
  const annotationsStyles = useRecoilValue(annotationStylesState);
  const isRangedAnnotationsLabEnabled = useIsLabEnabled(K.LAB.BETA_UI_RANGED_ANNOTATIONS);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // clear range selection on outside click if a selection was made.
  useOutsideEvent('mousedown', [containerRef], () => {
    if (selectionRange.start || selectionRange.end) {
      setSelectionRange({});
    }
  });

  /* Tooltip state */
  const annotationTooltipTransitionRef = useRef<number>();
  const [activeAnnotationData, setActiveAnnotationData] = useState<AnnotationData | null>(null);
  const stopAnnotationTooltipTransition = (): void => window.clearTimeout(annotationTooltipTransitionRef.current);
  const resetActiveAnnotation = (): void => setActiveAnnotationData(null);

  /* Annotation Modal */
  const [annotationModal, setAnnotationModal] = useState<{ show: boolean; annotation: null | Annotation }>({
    show: false,
    annotation: null,
  });
  const resetAnnotationModal = (): void => setAnnotationModal({ show: false, annotation: null });

  // Initialize dynamic chart events, merge into our custom config
  const [options, setOptions] = useState<Options>(
    merge(getScalyrGraphConfig(), {
      tooltip: {
        pointFormatter(): string {
          // @ts-expect-error
          // eslint-disable-next-line
          const series = this.series;
          // @ts-expect-error
          // eslint-disable-next-line
          const pointValue = this.y;
          let displayName = series.name || '';
          if (displayName.length > K.FACET_DISPLAY_NAME_MAX_LEN) {
            displayName = truncateText(displayName, K.FACET_DISPLAY_NAME_MAX_LEN, K.PLACEMENT.MIDDLE);
          }

          return `
          <tr>
            <td style="color: ${series.color}" class="GraphResults__TooltipPlotName">${displayName}</td>
            <td class="GraphResults__TooltipValue">${numeral(pointValue).format('0,0[.][00000] a').toUpperCase()}</td>
          </tr>
        `;
        },
      },
      chart: {
        events: {
          click(): void {
            setSelectionRange({ selectionBox: false });
          },
          selection(e: ChartSelectionContextObject): void {
            e.preventDefault();
            // If selection has been made, calculate left positioning for `Zoom In` button;
            const xMin = e.xAxis[0].axis.toPixels(Math.floor(e.xAxis[0].min), false);
            const xMax = e.xAxis[0].axis.toPixels(Math.floor(e.xAxis[0].max), false);
            const zoomX = xMin + (xMax - xMin) / 2;
            setSelectionRange({
              start: Math.floor(e.xAxis[0].min),
              end: Math.floor(e.xAxis[0].max),
              selectionBox: true,
              leftPosition: zoomX,
            });
            mouseDownRef.current = null;
          },
        },
      },
      plotOptions: {
        series: {
          point: {
            events: {
              mouseOver(e: Event): void {
                const target = e.target as unknown as HighchartsPoint;
                // if we have a mouseDownRef, then user is currently making a selection, so capture start and end.
                if (isNumber(mouseDownRef?.current) && isNumber(target?.x)) {
                  setSelectionRange({
                    start: Math.floor(Math.min(mouseDownRef.current, target.x)),
                    end: Math.floor(Math.max(mouseDownRef.current, target.x)),
                    selectionBox: false,
                  });
                }
              },
            },
          },
        },
        column: {
          states: {
            inactive: {
              enabled: false,
            },
          },
        },
        line: {
          marker: {
            // A neat trick to show a marker if there's just one point
            enabled: undefined,
            // Hide markers in all cases other cases (any large number would achieve this here)
            enabledThreshold: K.DEFAULT_AUTO_SEARCH_GRAPH_SLICES,
          },
        },
      },
    }),
  );
  // Listen for seriesData updates
  useEffect(() => {
    const plotBands: XAxisPlotBandsOptions[] = [];
    const plotLines: XAxisPlotLinesOptions[] = [];
    const min = chartRef?.current?.chart.yAxis[0].min;
    const max = chartRef?.current?.chart.yAxis[0].max;
    const series: SeriesPolygonOptions[] = [];
    if (annotationsData) {
      annotationsData.forEach((annotation: Annotation) => {
        const currentColor = annotationsStyles.find(
          (color: AnnotationStyles) => color.borderColor === annotation.color,
        );
        plotBands.push({
          color: currentColor?.backgroundColor,
          from: annotation.startMs,
          to: annotation.endMs,
        });
        const plotLineConfig = {
          color: annotation.color,
          width: K.CHART_PLOT_LINES_WIDTH,
          zIndex: K.CHART_PLOT_LINES_ZINDEX,
          dashStyle: K.CHART_PLOT_LINES_DASHSTYLE as XAxisPlotLinesOptions['dashStyle'],
        };
        plotLines.push(
          {
            ...plotLineConfig,
            value: annotation.startMs,
          },
          {
            ...plotLineConfig,
            value: annotation.endMs,
          },
        );
        series.push({
          name: annotation.comment,
          type: 'polygon',
          data: [
            [annotation.startMs, min],
            [annotation.endMs, min],
            [annotation.endMs, max],
            [annotation.startMs, max],
            [annotation.startMs, min],
          ],
          opacity: 0,
          states: {
            inactive: {
              opacity: 0,
            },
          },
          events: {
            mouseOver(): void {
              setActiveAnnotationData({
                annotation,
                graphSeries: this.data as TooltipPositionerPointObject[], // eslint-disable-line react/no-this-in-sfc
              });
            },
            mouseOut(): void {
              // Add a small delay so that user can hover over the tooltip and perform action (e.g. click edit)
              //   when the pointer enters the tooltip – timeout is cleaned up, tooltip not closed
              annotationTooltipTransitionRef.current = window.setTimeout(resetActiveAnnotation, 300);
            },
          },
          zIndex: 1,
        });
      });
    }
    // seriesData might have some series that were the same as past updates,
    // We make a deep copy to ensure highcharts can update without issue.
    setOptions(
      merge(
        cloneDeep({
          series: prepareSeries(series, seriesData),
          xAxis: {
            min: parseFloat(startTime),
            max: parseFloat(endTime),
            plotBands,
            plotLines,
          },
          yAxis: {
            type: (yScale as K.Y_SCALE_OPTIONS) || K.Y_SCALE_OPTIONS.LINEAR,
          },
          plotOptions: {
            series: {
              animation: false,
            },
          },
          tooltip: {
            headerFormat: '<table class="GraphResults__Tooltip"><tbody>',
          },
        }),
        highchartsStyles,
      ),
    );
  }, [seriesData, startTime, endTime, annotationsData, highchartsStyles, annotationsStyles, yScale]);

  useEffect(() => {
    // A selection was made, draw a plotBand to indicate the selection
    if (selectionRange.selectionBox) {
      chartRef?.current?.chart.axes[0].addPlotBand({
        className: 'ScalyrGraph__PlotBand',
        from: selectionRange.start,
        to: selectionRange.end,
        zIndex: K.CHART_SELECTION_ZINDEX,
        id: K.CHART_SELECTION_ID,
      });
    } else {
      chartRef?.current?.chart.axes[0].removePlotBand(K.CHART_SELECTION_ID);
    }
  }, [selectionRange]);

  const prevRemainingWidth = usePrevious(remainingWidth);
  const prevSearchLegendWidth = usePrevious(searchGraphLegendWidth);
  // Reflow the chart if our remaining width changes (sidebar closes) or search graph legend panel is resized
  const sidebarWidthChanged = !!prevRemainingWidth && remainingWidth !== prevRemainingWidth;
  const legendWidthChanged = !!prevSearchLegendWidth && searchGraphLegendWidth !== prevSearchLegendWidth;
  useConditionalEffect(() => {
    recalcBarWidth();
    chartRef?.current?.chart?.reflow();
  }, sidebarWidthChanged || legendWidthChanged);

  const prevChartRef = usePrevious(chartRef);
  // If our chartRef had changed (or it is our first chartRef), add the ref to global state
  useConditionalEffect(() => {
    setCurrentChartRef(chartRef);
  }, chartRef !== prevChartRef);

  const handleMouseDown = (e: PointerEventObject): void => {
    const currentChart = chartRef.current?.chart;
    if (currentChart) {
      mouseDownRef.current = currentChart.xAxis[0].toValue(e.chartX - currentChart.plotLeft, true);
    }
    if (selectionRange.selectionBox) {
      setSelectionRange({});
    }
  };

  const handleMouseUp = (): void => {
    mouseDownRef.current = null;
  };

  const onZoomButtonClick = (): void => {
    if (rangeStart && rangeEnd) {
      setSearchParams({
        [K.URL_PARAM.START_TIME]: rangeStart.toString(),
        [K.URL_PARAM.END_TIME]: rangeEnd.toString(),
      } as UrlParams);
      setSelectionRange({ selectionBox: false });
    }
  };

  return (
    <>
      <div ref={containerRef} className="ScalyrGraph">
        <ScalyrHighcharts
          ref={chartRef}
          options={options}
          highcharts={Highcharts}
          containerProps={{
            onMouseDown: (e: ContainerPropEvent): void => handleMouseDown(e.nativeEvent),
            onMouseUp: handleMouseUp,
            style: {
              height: '100%',
              width: remainingWidth - searchGraphLegendWidth,
            },
          }}
        />
        {selectionRange?.selectionBox && (
          <>
            <Button
              className="ScalyrGraph__ZoomButton"
              variant={ButtonVariant.PRIMARY}
              onClick={onZoomButtonClick}
              leftIcon={<ZoomInIcon fontSize="small" />}
              style={selectionRange.leftPosition ? { left: selectionRange.leftPosition } : undefined}
              testId={ELEMENT.searchGraphZoomIn}
            >
              Zoom In
            </Button>
            {isRangedAnnotationsLabEnabled ? (
              <Button
                className="ScalyrGraph__AnnotationButton"
                variant={ButtonVariant.PRIMARY}
                onClick={(): void => setAnnotationModal({ show: true, annotation: null })}
                leftIcon={<InsertDriveFile fontSize="small" />}
                style={selectionRange.leftPosition ? { left: selectionRange.leftPosition } : undefined}
                testId={ELEMENT.searchGraphAddAnnotation}
              >
                Add Annotation
              </Button>
            ) : null}
          </>
        )}
        {activeAnnotationData && (
          <AnnotationTooltip
            onMouseEnter={stopAnnotationTooltipTransition}
            onMouseLeave={resetActiveAnnotation}
            annotationData={activeAnnotationData}
            onEdit={(): void => setAnnotationModal({ show: true, annotation: activeAnnotationData.annotation })}
          />
        )}
      </div>
      <AnnotationModal
        showDialog={annotationModal.show}
        annotation={annotationModal.annotation}
        onClose={resetAnnotationModal}
      />
    </>
  );
}

export default ScalyrGraph;
